/**
 * ADR 0467 D1 — what a job's cargo may still do, by the JOB's status. **The one rule** for every cargo write:
 * add · edit (qty / stop / note / recipient) · remove · restore · item status.
 * plan: 4-Archives/plan/tms/0467-cargo-locked-on-finished-and-cancelled-jobs.md (F1 · F2 · F3 of ADR 0466)
 *
 * Original for the browsers (web + driver app). The API cannot load this package at run time (ADR 0150) and keeps a copy:
 * `apps/api/src/modules/cargo/lib/cargo-ticket-lock.ts`. Both are pinned to `cargo-ticket-lock.cases.json` (vitest + jest) ⇒ drift goes red.
 *
 * Where it applies (update this list when a new place uses it):
 * - api `cargo-items.service.ts` createPairsTx · updateTx · removeTx · restoreTx (`assertCargoWrite`) · setStatusTx (via `canTransition`)
 *   → also route-draft `cargo.add/note/status`, the driver app and bulk status, which all go through that service
 * - api `lib/cargo-item-status.ts` `canTransition` · shared `cargo-item-rules.ts` `canTransition` (the `status` field)
 * - web `app/(dashboard)/jobs/tickets/tabs/CargoTab.tsx` (buttons · menus · the lock strip)
 * Must not break: "ยกเลิกทั้งหมด" on a cancelled job (`only_cancelled`) · a completed job still records outcomes (QA = ก).
 */
export type CargoLockReason = "TICKET_COMPLETED" | "TICKET_CANCELLED" | "TICKET_CLOSED" | "TICKET_STATUS_UNKNOWN";
export type CargoLockStatus = "any" | "only_cancelled" | "none";
export interface CargoTicketLock {
  add: boolean;
  edit: boolean;
  remove: boolean;
  restore: boolean;
  status: CargoLockStatus;
  reason: CargoLockReason | null;
}
export type CargoWriteKind = "add" | "edit" | "remove" | "restore";

const OPEN: CargoTicketLock = { add: true, edit: true, remove: true, restore: true, status: "any", reason: null };
const SHUT = (status: CargoLockStatus, reason: CargoLockReason): CargoTicketLock => ({ add: false, edit: false, remove: false, restore: false, status, reason });

/** Every job status (API `JOB_STATUSES` — the API spec checks none is missing) + legacy `approved` (ADR 0220). */
export const CARGO_LOCK_BY_STATUS: Readonly<Record<string, CargoTicketLock>> = {
  draft: OPEN,
  assigned: OPEN,
  approved: OPEN, // legacy pre-start (ADR 0215 retired it · ADR 0220 keeps it until the prod migration)
  driver_accepted: OPEN,
  started: OPEN,
  in_progress: OPEN,
  completed: SHUT("any", "TICKET_COMPLETED"), // QA = ก — outcomes may still be recorded; a completed job cannot reopen
  cancelled: SHUT("only_cancelled", "TICKET_CANCELLED"),
  closed: SHUT("none", "TICKET_CLOSED"),
};

/** Unknown status (old data · a typo) → locked (fail-closed). */
export function cargoTicketLock(ticketStatus: string | null | undefined): CargoTicketLock {
  const hit = Object.prototype.hasOwnProperty.call(CARGO_LOCK_BY_STATUS, ticketStatus ?? "") ? CARGO_LOCK_BY_STATUS[ticketStatus as string] : null;
  return { ...(hit ?? SHUT("none", "TICKET_STATUS_UNKNOWN")) };
}
